/**
 * Elementi dello stage: forme, misure reali, colori e regole di conteggio.
 *
 * **Le misure vengono dal regolamento, non dall'occhio.** Fonte: FITDS,
 * Regolamento IPSC Handgun, edizione italiana gennaio 2024 — con le quote
 * delle sagome, che sono uno standard internazionale identico ovunque si
 * faccia IPSC. Le proporzioni precedenti erano sbagliate: la sagoma è
 * 450 × 570 mm, non 450 × 750, e con 0.60 invece di 0.79 di rapporto le
 * silhouette uscivano troppo strette e lunghe.
 *
 * La convenzione di disegno è quella delle **piantine di gara**, che è
 * deliberatamente mista e non una proiezione geometrica:
 *
 *  - i **bersagli si disegnano di fronte** — una sagoma IPSC vista dall'alto è
 *    un trattino, e un trattino non si riconosce. Di fronte si distingue al
 *    volo una carta da un popper da un piatto, ed è tutto quello che serve a
 *    chi allestisce;
 *  - **muri, fault line e box restano in pianta**, perché di loro conta dove
 *    stanno e quanto sono lunghi.
 *
 * Colori, dal regolamento e non dalla palette dell'app:
 *  - 4.1.2.1 — la zona punti di Target e Mini Target è **marrone**, tinta unita;
 *  - 4.1.2.2 — i metallici hanno tutta la superficie frontale di **un solo
 *    colore, preferibilmente bianco**;
 *  - 4.1.3 — i no-shoot portano una **X ben visibile** oppure un colore
 *    uniforme diverso; carta e metallo possono differire fra loro, ed è la
 *    convenzione citata dalla regola stessa: carta bianca, metallo giallo.
 *
 * Conteggio colpi: **carta × 2** (9.5.1, «i due migliori colpi valgono per il
 * conteggio»), **metallo × 1** (4.3.1.4, va colpito e deve cadere), no-shoot
 * zero.
 *
 * Spec: docs/shooting/13-piano-revisione-test-federico.md §2
 */

// ── Geometria delle sagome di carta ────────────────────────────────────────
//
// Le quote del regolamento sono riquadri di ingombro; la forma è l'ottagono.
// Si tengono come **frazioni** del riquadro, così ogni formato (Target, Mini,
// Micro) le eredita in proporzione corretta senza tre serie di numeri.
//
//   zona A  150 × 325 mm su 450 × 570  ->  0.333 × 0.570
//   zona C  300 × 450 mm su 450 × 570  ->  0.667 × 0.789
//   D       tutto il resto dentro il profilo

const IPSC_ZONE_A = { w: 150 / 450, h: 325 / 570 };
const IPSC_ZONE_C = { w: 300 / 450, h: 450 / 570 };

/**
 * Profilo dell'ottagono IPSC.
 *
 * I quattro angoli sono tagliati a **45°**, e i fianchi sono verticali a tutta
 * larghezza: non c'è margine laterale. Le due cose non sono estetica, sono il
 * vincolo che fa entrare le zone nel profilo — con un taglio più profondo (o
 * con i fianchi rientrati) la zona C, che è alta 450 su 570, uscirebbe dalla
 * sagoma in alto e in basso.
 *
 * Il taglio è espresso in **millimetri** e non in frazioni perché a 45° la
 * stessa misura vale in orizzontale e in verticale; su una Mini e su una
 * Micro si riscala in proporzione alla larghezza.
 */
const IPSC_CUT_MM = 115; // su una sagoma larga 450
const IPSC_CUT_RATIO = IPSC_CUT_MM / 450;

/** Sagoma IDPA: testa rettangolare, spalle, corpo ottagonale. */
const IDPA_PROFILE = {
	headW: 0.28,
	headY: 0.22,
	shoulderY: 0.33,
	hipY: 0.7,
	bottomEdge: 0.32,
	inset: 0.14,
};

/**
 * Il profilo dell'ottagono come path SVG, in coordinate del viewBox.
 *
 * Generato invece che scritto a mano perché i tre formati (Target, Mini,
 * Micro) sono lo stesso profilo a scale diverse: un path per formato li fa
 * divergere alla prima correzione.
 */
export const ipscOutlinePath = (w, h) => {
	// A 45° il taglio consuma la stessa misura in orizzontale e in verticale.
	const cut = w * IPSC_CUT_RATIO;
	return [
		`M${cut} 0`,
		`L${w - cut} 0`,
		`L${w} ${cut}`,
		`L${w} ${h - cut}`,
		`L${w - cut} ${h}`,
		`L${cut} ${h}`,
		`L0 ${h - cut}`,
		`L0 ${cut}`,
		"Z",
	].join(" ");
};

export const idpaOutlinePath = (w, h) => {
	const p = IDPA_PROFILE;
	const headL = w * (0.5 - p.headW / 2);
	const headR = w * (0.5 + p.headW / 2);
	const left = w * p.inset;
	const right = w * (1 - p.inset);
	const botL = w * (0.5 - p.bottomEdge / 2);
	const botR = w * (0.5 + p.bottomEdge / 2);
	return [
		`M${headL} 0`,
		`L${headR} 0`,
		`L${headR} ${h * p.headY}`,
		`L${right} ${h * p.shoulderY}`,
		`L${right} ${h * p.hipY}`,
		`L${botR} ${h}`,
		`L${botL} ${h}`,
		`L${left} ${h * p.hipY}`,
		`L${left} ${h * p.shoulderY}`,
		`L${headL} ${h * p.headY}`,
		"Z",
	].join(" ");
};

/**
 * Profilo del popper (Appendice C2), generato dalle quote.
 *
 * Il disegno precedente — testa tonda, corpo bombato, gambo — **sembrava un
 * omino**, ed è esattamente ciò che è stato detto al test. Il popper vero è
 * un'altra cosa: una **testa circolare** larga quanto il bersaglio, un collo
 * stretto subito sotto, e un **corpo a trapezio** che si allarga fino a una
 * base piatta. È la silhouette che chiunque abbia visto un poligono
 * riconosce al volo, e si ottiene da due numeri: larghezza e altezza.
 *
 * Il Mini Popper (⅔) ha le stesse proporzioni, quindi lo stesso generatore:
 * un path per formato divergerebbe alla prima correzione, come per la carta.
 * Le coordinate sono in millimetri del bersaglio reale.
 */
export const popperOutlinePath = (w, h) => {
	const r = w / 2; // la testa è larga quanto il popper
	const neckY = w * 0.93; // appena sotto la testa
	const neckHalf = w / 4; // il collo è metà della testa
	// La semi-larghezza del cerchio alla quota del collo: così il corpo parte
	// esattamente dal bordo della testa e il profilo non ha scalini.
	const dy = neckY - r;
	const joinHalf = Math.sqrt(Math.max(0, r * r - dy * dy));
	const half = Math.min(neckHalf, joinHalf);
	return [
		`M${r - half} ${neckY}`,
		// Testa: arco che passa sopra, da sinistra a destra.
		`A${r} ${r} 0 1 1 ${r + half} ${neckY}`,
		// Corpo: trapezio fino alla base piatta.
		`L${w} ${h}`,
		`L0 ${h}`,
		"Z",
	].join(" ");
};

/** Centro e raggio della zona di calibrazione del popper, in mm. */
export const popperCalibrationZone = (w) => ({
	cx: w / 2,
	cy: w / 2,
	r: w * 0.3,
});

// ── Colori di regolamento ──────────────────────────────────────────────────
const PAPER_BROWN = "#c8a37a"; // 4.1.2.1
const PAPER_BROWN_IDPA = "#b9a68c";
const STEEL_WHITE = "#f4f5f7"; // 4.1.2.2 «preferibilmente bianco»
const NO_SHOOT_PAPER = "#ffffff"; // 4.1.3, con la X
const NO_SHOOT_STEEL = "#facc15"; // 4.1.3, l'esempio del regolamento

export const SHOOTING_ELEMENTS = [
	// ── Carta IPSC ──
	// 450 × 570 mm (standard internazionale). Bordo a punteggio nullo 5 mm
	// (FITDS 4.2.2), 3 mm sui Mini.
	{
		value: "ipsc",
		label: "Sagoma IPSC",
		discipline: "ipsc",
		group: "target",
		family: "paper",
		widthM: 0.45,
		heightM: 0.57,
		shape: "paper_ipsc",
		color: PAPER_BROWN,
		zones: { a: IPSC_ZONE_A, c: IPSC_ZONE_C },
		borderMm: 5,
		shotsRequired: 2,
		// Famiglia di scala: il regolamento vieta di mescolare formati diversi
		// nello stesso gruppo di bersagli (4.1.1.2).
		scaleClass: "full",
	},
	{
		value: "mini_ipsc",
		label: "Mini IPSC",
		discipline: "ipsc",
		group: "target",
		family: "paper",
		// ≈ ⅔ del Target.
		widthM: 0.3,
		heightM: 0.375,
		shape: "paper_ipsc",
		color: PAPER_BROWN,
		zones: { a: IPSC_ZONE_A, c: IPSC_ZONE_C },
		borderMm: 3,
		shotsRequired: 2,
		scaleClass: "mini",
	},
	{
		value: "micro_ipsc",
		label: "Micro IPSC",
		discipline: "ipsc",
		group: "target",
		family: "paper",
		// ≈ 40% del Target. Citato dalle regole di combinazione (4.1.1.2,
		// 4.1.5) ma non quotato in Appendice B: la scala è quella d'uso.
		widthM: 0.18,
		heightM: 0.228,
		shape: "paper_ipsc",
		color: PAPER_BROWN,
		zones: { a: IPSC_ZONE_A, c: IPSC_ZONE_C },
		borderMm: 3,
		shotsRequired: 2,
		scaleClass: "micro",
	},
	{
		value: "no_shoot",
		label: "No-shoot carta",
		discipline: "ipsc",
		group: "target",
		family: "paper",
		widthM: 0.45,
		heightM: 0.57,
		shape: "paper_ipsc",
		// 4.1.3: colore uniforme diverso dai bersagli a punti, più la X.
		color: NO_SHOOT_PAPER,
		noShoot: true,
		shotsRequired: 0,
		scaleClass: "full",
	},
	{
		value: "bobber",
		label: "Bobber / swinger",
		discipline: "ipsc",
		group: "target",
		family: "paper",
		widthM: 0.45,
		heightM: 0.57,
		shape: "paper_ipsc",
		color: PAPER_BROWN,
		zones: { a: IPSC_ZONE_A, c: IPSC_ZONE_C },
		// Bersaglio mobile: il tratteggio alla base lo distingue da un fisso.
		moving: true,
		shotsRequired: 2,
		scaleClass: "full",
	},

	// ── Carta IDPA ──
	// Disciplina diversa, regolamento diverso: resta disponibile, ma marcata,
	// e la palette la mostra solo su un drill IDPA.
	{
		value: "idpa",
		label: "Sagoma IDPA",
		discipline: "idpa",
		group: "target",
		family: "paper",
		widthM: 0.45,
		heightM: 0.75,
		shape: "paper_idpa",
		color: PAPER_BROWN_IDPA,
		shotsRequired: 2,
		scaleClass: "full",
	},
	{
		value: "no_shoot_idpa",
		label: "No-shoot IDPA",
		discipline: "idpa",
		group: "target",
		family: "paper",
		widthM: 0.45,
		heightM: 0.75,
		shape: "paper_idpa",
		color: NO_SHOOT_PAPER,
		noShoot: true,
		shotsRequired: 0,
		scaleClass: "full",
	},

	// ── Metallo ──
	// Popper 850 mm, Mini Popper 560 mm (⅔). Tolleranza ±5 mm (App. C2).
	{
		value: "popper",
		label: "Popper",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		widthM: 0.3,
		heightM: 0.85,
		shape: "steel_popper",
		color: STEEL_WHITE,
		shotsRequired: 1,
		scaleClass: "full",
	},
	{
		value: "mini_popper",
		label: "Mini popper",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		widthM: 0.2,
		heightM: 0.56,
		shape: "steel_popper",
		color: STEEL_WHITE,
		shotsRequired: 1,
		scaleClass: "mini",
	},
	// Piatti, Handgun: tondi 20-30 cm Ø, rettangolari 15×15 – 30×30 cm
	// (Appendice C3). Vanno posti su hard cover o paletti di almeno 1 m.
	{
		value: "plate",
		label: "Piatto Ø20",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		widthM: 0.2,
		heightM: 0.2,
		shape: "steel_plate",
		color: STEEL_WHITE,
		shotsRequired: 1,
		scaleClass: "any",
	},
	{
		value: "plate_large",
		label: "Piatto Ø30",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		widthM: 0.3,
		heightM: 0.3,
		shape: "steel_plate",
		color: STEEL_WHITE,
		shotsRequired: 1,
		scaleClass: "any",
	},
	{
		value: "plate_rect",
		label: "Piatto 15×15",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		widthM: 0.15,
		heightM: 0.15,
		shape: "steel_plate_rect",
		color: STEEL_WHITE,
		shotsRequired: 1,
		scaleClass: "any",
	},
	{
		value: "plate_rect_large",
		label: "Piatto 30×30",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		widthM: 0.3,
		heightM: 0.3,
		shape: "steel_plate_rect",
		color: STEEL_WHITE,
		shotsRequired: 1,
		scaleClass: "any",
	},
	{
		value: "plate_rack",
		label: "Plate rack",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		// Sei piatti su telaio: il regolamento non lo quota, la misura è
		// quella d'uso. Conta come sei bersagli.
		widthM: 1.5,
		heightM: 0.5,
		shape: "steel_rack",
		color: STEEL_WHITE,
		shotsRequired: 6,
		scaleClass: "any",
	},
	{
		value: "no_shoot_metal",
		label: "No-shoot metallo",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		widthM: 0.3,
		heightM: 0.3,
		shape: "steel_plate",
		color: NO_SHOOT_STEEL,
		noShoot: true,
		shotsRequired: 0,
		scaleClass: "any",
	},

	// ── Elementi di campo (in pianta) ──
	{
		value: "wall",
		label: "Muro",
		group: "layout",
		widthM: 2,
		heightM: 0.12,
		shape: "panel",
		color: "#6b7280",
		resizable: true,
	},
	{
		value: "barricade",
		label: "Barricata",
		group: "layout",
		widthM: 1.2,
		heightM: 0.12,
		shape: "barricade",
		color: "#8a6f4e",
		resizable: true,
	},
	{
		value: "port",
		label: "Finestra / porta",
		group: "layout",
		widthM: 0.8,
		heightM: 0.12,
		shape: "port",
		color: "#60a5fa",
		resizable: true,
	},
	{
		value: "fault_line",
		label: "Fault line",
		group: "layout",
		// Minimo regolamentare: 1.5 m (regola 2.2.1.4).
		widthM: 1.5,
		heightM: 0.06,
		shape: "fault",
		color: "#e05252",
		resizable: true,
	},
	{
		value: "shooting_box",
		label: "Box di tiro",
		group: "layout",
		widthM: 1,
		heightM: 1,
		shape: "box",
		color: "#facc15",
		resizable: true,
	},
	{
		value: "start_box",
		label: "Box di partenza",
		group: "layout",
		widthM: 1,
		heightM: 1,
		shape: "box",
		color: "#00d68f",
		resizable: true,
	},
	{
		value: "table",
		label: "Tavolo",
		group: "layout",
		widthM: 1.2,
		heightM: 0.6,
		shape: "table",
		color: "#9ca3af",
		resizable: true,
	},
	{
		value: "activator",
		label: "Attivatore",
		group: "layout",
		widthM: 0.3,
		heightM: 0.3,
		shape: "activator",
		color: "#f59e0b",
	},

	// ── Fuori regolamento, tenuti per i drill già salvati ──
	// Non compaiono nella palette: `elementSpec` ricade sul primo elemento per
	// un tipo sconosciuto, e cancellarli trasformerebbe in silenzio un popper
	// salvato in una sagoma.
	{
		value: "popper_large",
		label: "Popper grande",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		widthM: 0.4,
		heightM: 1.2,
		shape: "steel_popper",
		color: STEEL_WHITE,
		shotsRequired: 1,
		scaleClass: "any",
		// Non esiste in Appendice C2: fuori dalla palette, leggibile se c'è.
		legacy: true,
	},
	{
		value: "steel",
		label: "Metallo generico",
		discipline: "ipsc",
		group: "target",
		family: "steel",
		widthM: 0.3,
		heightM: 0.3,
		shape: "steel_plate",
		color: STEEL_WHITE,
		shotsRequired: 1,
		scaleClass: "any",
		legacy: true,
	},
];

export const elementSpec = (value) =>
	SHOOTING_ELEMENTS.find((element) => element.value === value) ||
	SHOOTING_ELEMENTS[0];

/**
 * Come `elementSpec`, ma **senza** ripiego: `undefined` per un tipo che non
 * esiste. Serve a chi deve scrivere un'etichetta — ripiegare sulla sagoma
 * IPSC trasformerebbe un «Altro» in una «Sagoma IPSC» senza che si veda.
 */
export const findElement = (value) =>
	SHOOTING_ELEMENTS.find((element) => element.value === value);

export const TARGET_ELEMENTS = SHOOTING_ELEMENTS.filter(
	(element) => element.group === "target" && !element.legacy,
);
export const LAYOUT_ELEMENTS = SHOOTING_ELEMENTS.filter(
	(element) => element.group === "layout",
);

/**
 * La palette per una disciplina.
 *
 * **Le sagome di una disciplina non entrano nella palette di un'altra.** Una
 * IDPA dentro uno stage IPSC non è una scelta, è un errore — e soprattutto
 * non deve finire nelle validazioni di regolamento qui sotto, che sono regole
 * IPSC. Gli elementi di campo (muri, box, tavoli) non hanno disciplina: sono
 * gli stessi ovunque.
 *
 * Vale l'invariante di repo: **la costante resta completa** e si filtra al
 * momento di rendere la palette, mai amputando `SHOOTING_ELEMENTS` — o un
 * drill salvato con una sagoma IDPA smetterebbe di sapersi disegnare.
 */
export const paletteFor = (discipline) => {
	const wanted = String(discipline || "ipsc").toLowerCase();
	const match = (element) =>
		!element.discipline || element.discipline === wanted;
	return {
		paper: TARGET_ELEMENTS.filter((e) => e.family === "paper" && match(e)),
		steel: TARGET_ELEMENTS.filter((e) => e.family === "steel" && match(e)),
		layout: LAYOUT_ELEMENTS,
	};
};

/** Compatibilità: le vecchie liste piatte, senza filtro di disciplina. */
export const PAPER_ELEMENTS = TARGET_ELEMENTS.filter((e) => e.family === "paper");
export const STEEL_ELEMENTS = TARGET_ELEMENTS.filter((e) => e.family === "steel");

// ── Conteggio colpi ────────────────────────────────────────────────────────

/**
 * Colpi che contano di default su un bersaglio: carta × 2, metallo × 1.
 *
 * **Sono numeri di regolamento, non convenzioni dell'app.** FITDS 9.5.1: i
 * bersagli di carta si ingaggiano con almeno un colpo e «i due migliori colpi
 * valgono per il conteggio dei punti»; il metallo (4.3.1.4) va colpito e deve
 * cadere. Il gemello Python sta in `backend/models/shooting.py`.
 */
export const defaultHitsRequired = (targetType) =>
	elementSpec(targetType).shotsRequired || 0;

/**
 * Colpi **esatti** richiesti dalla disposizione.
 *
 * Era un minimo, e il minimo era il difetto: `rounds_planned` restava un campo
 * libero che nessuno confrontava al rialzo, quindi si poteva prevedere più
 * colpi di quanti lo stage ne ammettesse senza che nulla lo segnalasse. Con
 * `hits_required` sul singolo bersaglio il numero è esatto, e lo scarto in
 * qualunque direzione è un'informazione.
 */
export const requiredShots = (targets) =>
	(targets || []).reduce((total, target) => {
		const required =
			target.hits_required ?? defaultHitsRequired(target.target_type);
		return total + required * (target.count || 1);
	}, 0);

/**
 * Numera i bersagli T1, T2, … rispettando quelli che hanno già un numero.
 *
 * Il numero è l'identità con cui la scheda di punteggio riaggancia l'impatto
 * al bersaglio. I drill salvati prima di `target_no` non ce l'hanno: si
 * assegna in lettura dall'indice, così non serve migrare niente.
 */
export const assignTargetNumbers = (targets) => {
	let next = 0;
	return (targets || []).map((target) => {
		// I no-shoot non si sparano: non entrano nella numerazione della
		// scheda, o il tiratore cerca un T4 che non deve compilare.
		if (defaultHitsRequired(target.target_type) === 0) return target;
		next += 1;
		return { ...target, target_no: target.target_no ?? next };
	});
};

/**
 * Collassa i bersagli identici in righe `3× Sagoma IPSC @10m`.
 *
 * Serve perché il planner produce **una riga per sagoma trascinata**: tre
 * sagome uguali alla stessa distanza sono tre righe `count: 1`, e in lettura
 * l'atleta se le vedeva elencate tre volte. Si raggruppa per (tipo, distanza)
 * perché sono le due cose che distinguono un bersaglio dall'altro per chi
 * deve allestire; posizione e rotazione no, quelle le dice la piantina.
 *
 * Non si usa in scrittura: il planner continua a lavorare sui singoli.
 */
export const groupTargets = (targets) => {
	const groups = new Map();
	for (const target of targets || []) {
		const key = `${target.target_type}|${target.distance_m ?? ""}|${
			target.hits_required ?? ""
		}`;
		const existing = groups.get(key);
		if (existing) existing.count += target.count || 1;
		else groups.set(key, { ...target, count: target.count || 1 });
	}
	return [...groups.values()];
};

/** Distanza di un bersaglio dal box di partenza, se entrambi sono posizionati. */
export const distanceFromStart = (target, startBox) => {
	if (!startBox || target?.x_m === null || target?.x_m === undefined)
		return null;
	if (target?.y_m === null || target?.y_m === undefined) return null;
	const dx = target.x_m - (startBox.x_m || 0);
	const dy = target.y_m - (startBox.y_m || 0);
	return Math.round(Math.sqrt(dx * dx + dy * dy) * 10) / 10;
};

// ── Validazioni di allestimento ────────────────────────────────────────────

/**
 * Classificazione dell'esercizio per numero di colpi (FITDS 1.2.1).
 *
 * Short ≤ 12 · Medium ≤ 24 · Long ≤ 32. Oltre i 32 non è un esercizio
 * riconosciuto: in allenamento si fa lo stesso, ma vale la pena dirlo.
 */
export const courseClass = (shots) => {
	if (!shots) return null;
	if (shots <= 12) return { value: "short", label: "Short Course" };
	if (shots <= 24) return { value: "medium", label: "Medium Course" };
	if (shots <= 32) return { value: "long", label: "Long Course" };
	return { value: "over", label: "Oltre i 32 colpi" };
};

/**
 * Cosa non torna in questo allestimento, secondo il regolamento.
 *
 * **Avvisi, mai divieti.** In allenamento si allestisce anche fuori
 * regolamento di proposito — un esercizio di sola precisione su piatti è
 * legittimo come lavoro e vietato come stage di gara. L'avviso è
 * un'informazione per chi programma, non un cancello.
 *
 * Pura e senza stato: è testabile e la usano sia il designer sia, un domani,
 * la validazione lato server.
 */
export const validateStage = (prescription = {}) => {
	const targets = prescription.targets || [];
	const warnings = [];
	if (targets.length === 0) return warnings;

	const specs = targets.map((t) => elementSpec(t.target_type));
	const scoring = targets.filter((t, i) => !specs[i].noShoot);
	const scoringSpecs = scoring.map((t) => elementSpec(t.target_type));

	// 4.3.3.3 — i piatti non possono essere l'unico tipo di bersaglio.
	const hasPaperOrPopper = scoringSpecs.some(
		(s) => s.family === "paper" || s.shape === "steel_popper",
	);
	if (scoringSpecs.length > 0 && !hasPaperOrPopper) {
		warnings.push({
			rule: "4.3.3.3",
			text: "I piatti non possono essere l'unico tipo di bersaglio: serve almeno una sagoma di carta o un popper che assegni punti.",
		});
	}

	// 4.1.1.2 — formati di scala diversi non si mescolano nello stesso gruppo.
	const paperScales = new Set(
		scoringSpecs.filter((s) => s.family === "paper").map((s) => s.scaleClass),
	);
	if (paperScales.size > 1) {
		warnings.push({
			rule: "4.1.1.2",
			text: "Sagome di formati diversi (Target, Mini, Micro) non vanno associate nello stesso gruppo di bersagli.",
		});
	}
	const popperScales = new Set(
		scoringSpecs
			.filter((s) => s.shape === "steel_popper")
			.map((s) => s.scaleClass),
	);
	if (popperScales.size > 1) {
		warnings.push({
			rule: "4.1.1.2",
			text: "Popper e Mini Popper non vanno associati nello stesso gruppo di bersagli.",
		});
	}

	// 1.2.1 — classificazione e tetto dei colpi.
	const shots = requiredShots(targets);
	const klass = courseClass(shots);
	if (klass?.value === "over") {
		warnings.push({
			rule: "1.2.1.3",
			text: `${shots} colpi: un Long Course non ne prevede più di 32.`,
		});
	}

	// Lo scarto fra colpi previsti e colpi che lo stage ammette. È il difetto
	// segnalato al test: si poteva prevedere più colpi di quanti ce ne
	// stessero, e nulla lo diceva.
	const planned = prescription.rounds_planned;
	if (planned && shots && planned !== shots) {
		warnings.push({
			rule: "9.5.1",
			text:
				planned > shots
					? `Colpi previsti ${planned}, ma i bersagli ne ammettono ${shots}.`
					: `Colpi previsti ${planned}, ma i bersagli ne richiedono ${shots}.`,
		});
	}

	return warnings;
};
